/**
 * Group trade order failures come back in `failed_reason` as one of:
 *   - a plain message, e.g. "403: User does not have a position for the symbol."
 *   - an HTTP code followed by a Python-repr dict, e.g.
 *     "403: {'message': '...', 'worst_case_strategies': [...], 'worst_case_orders': [...]}"
 *
 * The dict form is not valid JSON (single quotes, True/False/None, and embedded
 * double quotes inside string values), so we convert it before parsing and pull
 * out the worst-case analysis ("ME data") for the reuse of WorstCaseAnalysis.vue.
 */

/**
 * Convert a Python literal (dict/list) string into valid JSON. Handles
 * single-quoted strings containing embedded double quotes (e.g. marginDebug),
 * escaped quotes, and the True/False/None keywords.
 * @param {string} input
 * @returns {string} JSON string
 */
export function pythonLiteralToJson(input) {
  let out = ''
  let i = 0
  const n = input.length

  while (i < n) {
    const ch = input[i]

    // String literal — Python uses ' or "; emit a JSON double-quoted string.
    if (ch === "'" || ch === '"') {
      const quote = ch
      out += '"'
      i++
      while (i < n) {
        const c = input[i]
        if (c === '\\') {
          const next = input[i + 1]
          // \' is a literal apostrophe in JSON, no escape needed.
          if (next === "'") {
            out += "'"
          } else {
            out += c + (next ?? '')
          }
          i += 2
          continue
        }
        if (c === quote) {
          i++
          break
        }
        // A double quote inside the value must be escaped for JSON.
        out += c === '"' ? '\\"' : c
        i++
      }
      out += '"'
      continue
    }

    // Bare keywords outside of strings.
    if (/[A-Za-z_]/.test(ch)) {
      let j = i
      while (j < n && /[A-Za-z0-9_]/.test(input[j])) j++
      const word = input.slice(i, j)
      if (word === 'True') out += 'true'
      else if (word === 'False') out += 'false'
      else if (word === 'None') out += 'null'
      else out += word
      i = j
      continue
    }

    out += ch
    i++
  }

  return out
}

/**
 * Parse a group trade `failed_reason` into a display message and worst-case data.
 * @param {string} reason
 * @returns {{
 *   message: string,
 *   worstCaseOrders: Array,
 *   worstCaseStrategies: Array,
 *   hasMeData: boolean,
 * }}
 */
export function parseFailedReason(reason) {
  const empty = {
    message: reason || '',
    worstCaseOrders: [],
    worstCaseStrategies: [],
    hasMeData: false,
  }

  if (!reason || typeof reason !== 'string') return empty

  const braceIndex = reason.indexOf('{')
  if (braceIndex === -1) return empty

  try {
    const dict = reason.slice(braceIndex)
    const parsed = JSON.parse(pythonLiteralToJson(dict))
    if (!parsed || typeof parsed !== 'object') return empty

    const worstCaseOrders = Array.isArray(parsed.worst_case_orders)
      ? parsed.worst_case_orders
      : []
    const worstCaseStrategies = Array.isArray(parsed.worst_case_strategies)
      ? parsed.worst_case_strategies
      : []

    return {
      // Prefer the human-readable message; fall back to the raw reason.
      message: parsed.message || reason,
      worstCaseOrders,
      worstCaseStrategies,
      hasMeData: worstCaseOrders.length > 0 || worstCaseStrategies.length > 0,
    }
  } catch (e) {
    return empty
  }
}
